/**
 * Queue state for one question.
 *
 * Nothing here persists. Skips are a statement about this sitting, not
 * about the question, so a skip that survived a reload would be the
 * persistent dismiss this panel deliberately does not have.
 */

import { useCallback, useMemo, useState } from "react"

import {
  leoSpottedOverlayKeys,
  reopenLeoSpottedFindings,
  resolveLeoSpottedFindings,
} from "@/lib/library-bank-row-overlays"
import {
  buildLeoSpottedQueue,
  finishResolvedLeoSpottedCard,
  goToLeoSpottedCard,
  openLeoSpottedCards,
  reopenLeoSpottedCard,
  resolveLeoSpottedCard,
  skipLeoSpottedCard,
  type LeoSpottedCard,
  type LeoSpottedQueue,
  type LeoSpottedQueueInput,
} from "./leo-spotted-queue"

/** Matches `.leo-spotted-card-settle` in leo-blob-animations.css */
export const LEO_SPOTTED_RESOLVE_SETTLE_MS = 560

export function leoSpottedCardsForIssueIds(
  queue: LeoSpottedQueue,
  issueIds: readonly string[],
): LeoSpottedCard[] {
  const targets = new Set(issueIds.filter(Boolean))
  if (targets.size === 0) return []
  const seen = new Set<string>()
  const cards: LeoSpottedCard[] = []
  for (const card of queue.cards) {
    if (card.state !== "open") continue
    const matches =
      targets.has(card.id) ||
      card.issues.some((issue) => targets.has(issue.id))
    if (!matches || seen.has(card.id)) continue
    seen.add(card.id)
    cards.push(card)
  }
  return cards
}

export function useLeoSpottedQueue(
  questionId: string,
  input: LeoSpottedQueueInput,
  options?: { reduceMotion?: boolean },
): {
  queue: LeoSpottedQueue
  navigate: (index: number) => void
  resolve: (card: LeoSpottedCard) => void
  skip: (card: LeoSpottedCard) => void
  reopen: (card: LeoSpottedCard) => void
} {
  const reduceMotion = options?.reduceMotion ?? false
  // What the question said when the queue was built. A fresh audit rebuilds
  // it; a re-render with the same findings must not throw away the author's
  // skips, which is why this is not a `useMemo` over `input`.
  const signature = JSON.stringify({
    issues: input.issues?.map((issue) => issue.id) ?? [],
    timesUsed: input.timesUsed,
    pbi: input.pbi,
    bloom: input.bloom,
    difficulty: input.difficulty,
    category: input.categoryIds?.length ?? 0,
    unassigned: input.categoryUnassigned,
  })

  const built = useMemo(
    () =>
      buildLeoSpottedQueue(input),
    // `signature` is the content; rebuilding on object identity would reset
    // the queue on every keystroke in the editor behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionId, signature],
  )

  const [override, setOverride] = useState<LeoSpottedQueue | null>(null)
  const [overrideForQuestion, setOverrideForQuestion] = useState(questionId)
  if (overrideForQuestion !== questionId) {
    setOverrideForQuestion(questionId)
    setOverride(null)
  }

  // React's own "adjusting state when a prop changes" pattern: compare
  // against state, not a ref. Mutating a ref in the render body is a side
  // effect during render, which React may run twice or discard.
  const [builtFor, setBuiltFor] = useState(signature)
  if (builtFor !== signature) {
    setBuiltFor(signature)
    // Keep override through a resolve: clearing here dropped the check card
    // before the settle animation and stack reorder could run.
  }

  const queue = override ?? built

  const navigate = useCallback(
    (index: number) => setOverride(goToLeoSpottedCard(queue, index)),
    [queue],
  )
  const resolve = useCallback(
    (card: LeoSpottedCard) => {
      // A finding the reading re-derives from the question clears when
      // the question clears. Recording it resolved would hide the card
      // now and show it again, open, on the next read — the author is
      // told they fixed it and then asked to fix it again. Leave it
      // alone and let the next read decide.
      const issueIds =
        card.issues.length > 0 ? card.issues.map((issue) => issue.id) : [card.id]
      const settleMs = reduceMotion ? 0 : LEO_SPOTTED_RESOLVE_SETTLE_MS

      setOverride(resolveLeoSpottedCard(queue, card.id))

      window.setTimeout(() => {
        if (!card.selfClearing) {
          resolveLeoSpottedFindings(
            questionId,
            issueIds,
            leoSpottedOverlayKeys(questionId),
          )
        }
        setOverride((current) => {
          const base = current ?? queue
          if (openLeoSpottedCards(base).length === 0) {
            return resolveLeoSpottedCard(base, card.id)
          }
          return finishResolvedLeoSpottedCard(base, card.id)
        })
      }, settleMs)
    },
    [queue, questionId, reduceMotion],
  )
  const skip = useCallback(
    (card: LeoSpottedCard) => setOverride(skipLeoSpottedCard(queue, card.id)),
    [queue],
  )
  const reopen = useCallback(
    (card: LeoSpottedCard) => {
      reopenLeoSpottedFindings(
        questionId,
        card.issues.length > 0 ? card.issues.map((issue) => issue.id) : [card.id],
        leoSpottedOverlayKeys(questionId),
      )
      setOverride(reopenLeoSpottedCard(queue, card.id))
    },
    [queue, questionId],
  )

  return { queue, navigate, resolve, skip, reopen }
}
